import { useStaticQueryWithPreview } from "@tedgustaf/gatsby-plugin-sanity-preview";
import classNames from "classnames";
import { graphql, useStaticQuery } from "gatsby";
import React, { useEffect, useMemo } from "react";
import siteConfig from "../config/siteConfig";
import { SiteSettingsContextProvider } from "../context/SiteSettingsContext";
import { GatsbyPageContext } from "../models/pages/GatsbyPageContext";
import { SiteSettings } from "../models/shared/SiteSettings";
import { siteSettingsPreviewQueryProjection } from "../preview/staticQueries/siteSettings";
import { captureUtmParameters } from "../utilities/gtm";
import { compareLanguages } from "../utilities/routes/utilities";
import { GroupFooter } from "./Footer/GroupFooter";
import { MarketFooter } from "./Footer/MarketFooter";
import { GroupHeader } from "./Header/GroupHeader";
import { MarketHeader } from "./Header/MarketHeader";
import { MarketSelectorDialog } from "./MarketSelectorDialog";
import { SiteWideBanner } from "./SiteWideBanner";

export type SiteSettingsQuery = {
  allSanitySiteSettings?: {
    nodes?: SiteSettings[];
  };
};

interface Props {
  breadcrumbs?: JSX.Element;
  children?: React.ReactNode | ((siteSettings: SiteSettings) => React.ReactNode);
  hero?: JSX.Element | boolean;
  pageContext: GatsbyPageContext;
  isStartPage?: boolean;
  transparentHeader?: boolean;
  shouldHideBanner?: boolean;
  isFixedHeader?: boolean;
}

const Layout: React.FC<Props> = ({
  breadcrumbs,
  children,
  hero,
  isStartPage = false,
  pageContext,
  transparentHeader = false,
  shouldHideBanner = false,
  isFixedHeader = false
}) => {
  const siteSettingsQuery = useStaticQueryWithPreview({
    staticQueryData: useStaticQuery<SiteSettingsQuery>(graphql`
      query sanitySiteSettings {
        ...SanitySiteSettingsFragment
      }
    `),
    query: siteSettingsPreviewQueryProjection,
    options: useMemo(() => ({ addRawFields: true }), [])
  });

  const siteSettings =
    siteSettingsQuery.allSanitySiteSettings?.nodes?.find(node =>
      compareLanguages(node.i18nLang, pageContext.i18nLang)
    ) ?? ({} as SiteSettings);

  const { siteWideBanner } = siteSettings;

  const showBanner = siteWideBanner && !shouldHideBanner;

  useEffect(() => {
    captureUtmParameters();
  }, []);

  return (
    <SiteSettingsContextProvider siteSettings={siteSettings}>
      <div className="flex flex-col relative min-h-screen text-brand-primary-20 font-anticimex-default">
        {siteConfig.isGroup ? (
          <GroupHeader
            key="header"
            siteSettings={siteSettings}
            transparent={transparentHeader}
          />
        ) : (
          <MarketHeader
            key="header"
            siteSettings={siteSettings}
            isFixed={isFixedHeader}
          />
        )}

        {showBanner && (
          <SiteWideBanner
            buttonLink={siteWideBanner.buttonLink ?? undefined}
            color={siteWideBanner.color ?? undefined}
            currentPagePath={pageContext.currentPath}
            enabled={siteWideBanner.enabled ?? undefined}
            isSiteWide={siteWideBanner.isSiteWide ?? undefined}
            isStartPage={isStartPage}
            isPermanent={siteWideBanner.isPermanent ?? undefined}
            text={siteWideBanner.text ?? undefined}
          />
        )}

        {breadcrumbs}
        {hero}

        <main
          className={classNames(
            "flex-1 w-full mx-auto py-8 lg:py-16 h-auto",
            showBanner ? "scroll-m-28 md:scroll-m-24" : ""
          )}
          id="main-content"
        >
          <section className="relative">
            {typeof children === "function" ? children(siteSettings) : children}
          </section>
        </main>

        {siteConfig.isGroup ? (
          <GroupFooter key="footer" siteSettings={siteSettings} />
        ) : (
          <MarketFooter key="footer" siteSettings={siteSettings} />
        )}
        <MarketSelectorDialog />
      </div>
    </SiteSettingsContextProvider>
  );
};

export default Layout;
